import { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { AppShell } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { LoaderOverlay } from '@/components/LoaderOverlay';
import { Navigation } from '@/components/Navigation/Navigation';
import { MainHeader } from '@/components/MainHeader';
import { useImpersonationInactivityTimeout, useScreenSize, useSessionStorage } from '@/hooks';
import { LimitedClusterSubRouteGuard } from '@/routing/LimitedClusterSubRouteGuard';
import { HEADER_HEIGHT, SIDE_NAVBAR_WIDTH } from '@/constants';
import { MESSAGE_STATUS } from '@/types';
import styles from './MainLayout.module.sass';
import classNames from 'classnames';

type MainLayoutProps = {
  className?: string;
};

export type MainLayoutContext = {
  setMessagesUnreadCount: (count: number, type: MESSAGE_STATUS) => void;
};

export const MainLayout = ({ className }: MainLayoutProps) => {
  const { isDesktop } = useScreenSize();
  const [messagesCounters, setMessagesCounters] = useState<Partial<Record<MESSAGE_STATUS, number>>>({});

  useImpersonationInactivityTimeout();

  const [isMobileMenuOpened, { toggle: toggleMobileMenu, open: openMobileMenu, close: closeMobileMenu }] =
    useDisclosure(isDesktop);

  const [isDesktopMenuExpanded, setIsDesktopMenuExpanded] = useSessionStorage('isDesktopMenuExpanded', true);

  const setMessagesUnreadCount = useCallback((count: number, type: MESSAGE_STATUS) => {
    setMessagesCounters((prev) => {
      if (prev[type] === count) return prev;
      return { ...prev, [type]: count };
    });
  }, []);

  const contextValue: MainLayoutContext = useMemo(() => ({ setMessagesUnreadCount }), [setMessagesUnreadCount]);

  useEffect(() => {
    if (isDesktop) {
      openMobileMenu();
    } else {
      closeMobileMenu();
    }
  }, [isDesktop]);

  return (
    <AppShell
      classNames={{
        root: classNames({ [styles.withCollapsedDesktopMenu]: !isDesktopMenuExpanded }),
        navbar: styles.navbar,
      }}
      layout="alt"
      navbar={{
        width: SIDE_NAVBAR_WIDTH,
        breakpoint: 'sm',
        collapsed: { mobile: !isMobileMenuOpened, desktop: !isMobileMenuOpened },
      }}
      header={{ height: HEADER_HEIGHT }}
      padding={0}
      className={className}
    >
      <MainHeader
        isMobileMenuOpened={isMobileMenuOpened}
        toggleMobileMenu={toggleMobileMenu}
        isDesktopMenuExpanded={isDesktopMenuExpanded}
        toggleDesktopMenuExpanded={() => setIsDesktopMenuExpanded((prev) => !prev)}
      />
      <Navigation
        isMobileMenuOpened={isMobileMenuOpened}
        isDesktopMenuExpanded={isDesktopMenuExpanded}
        toggleMobileMenu={toggleMobileMenu}
        messagesCounters={messagesCounters}
      />
      <AppShell.Main>
        <LimitedClusterSubRouteGuard>
          <Suspense fallback={<LoaderOverlay />}>
            <Outlet context={contextValue} />
          </Suspense>
        </LimitedClusterSubRouteGuard>
      </AppShell.Main>
    </AppShell>
  );
};
